// Excel-style per-column filter dropdown for the "View LoanTape" dialog -
// ported from reporting-hub/frontend/src/components/preflight/
// loantape-preview/ColumnFilterPopover.tsx. Options are computed client-side
// from the dialog's LoanTape snapshot (loantapeFilters.js
// getColumnFilterOptions(), a port of the old loantape-filter-options
// endpoint). Either a checkbox value list or a min/max range, depending on
// what the backend's `infer_filter_kind()` decided for this column (date columns always get "date_range"; numeric
// gets "values" if <=20 distinct values, else "number_range"; string always
// gets "values" - the kind itself never flips based on active filters, but
// the values/min-max shown ARE cascaded against every OTHER active filter
// plus the current search). Edits are local ("draft") until "Apply" is
// clicked, matching real Excel's AutoFilter flow - closing the overlay any
// other way (outside click, Escape) discards the draft for free via
// PrimeReact OverlayPanel's own default behavior.
//
// IMPORTANT semantics carried over exactly: for "values" kind, a `null`
// entry in the committed `values` array means "(Blanks)" was checked -
// UNCHECKING it actively EXCLUDES blank rows (like real Excel AutoFilter),
// it does not mean "blanks included by default".

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { OverlayPanel } from 'primereact/overlaypanel';
import { Button } from 'primereact/button';
import { Checkbox } from 'primereact/checkbox';
import { InputText } from 'primereact/inputtext';
import { InputNumber } from 'primereact/inputnumber';
import { Calendar } from 'primereact/calendar';
import { ScrollPanel } from 'primereact/scrollpanel';
import { getColumnFilterOptions } from './loantapeFilters';
import { formatCurrency, isoToDate, dateToIso } from '../calculation/calcFormat';

const BLANK_LABEL = '(Blanks)';

function formatOptionValue(value) {
  if (value === null) return BLANK_LABEL;
  return typeof value === 'number' ? formatCurrency(value) : String(value);
}

// Memoized: this renders once per LoanTape column (dozens of instances) as
// a child of the results table's header row. Without memo, every keystroke
// in the search box (which lives in the parent's un-debounced `query`
// state) re-executes every single instance's render function even though
// almost none of their props actually changed that render.
const ColumnFilterPopover = React.memo(function ColumnFilterPopover({
  snapshot,
  column,
  activeFilters,
  debouncedQuery,
  searchColumn,
  onApply,
}) {
  const overlayRef = useRef(null);
  const [open, setOpen] = useState(false);
  const activeFilter = activeFilters.find((f) => f.column === column) || null;
  const isActive = !!activeFilter;

  const otherFilters = useMemo(() => activeFilters.filter((f) => f.column !== column), [activeFilters, column]);

  // Computed only while the popover is open (dozens of these exist, one per
  // column); cascaded against every other active filter plus the search.
  const { options, optionsError } = useMemo(() => {
    if (!open || !snapshot) return { options: null, optionsError: null };
    try {
      return { options: getColumnFilterOptions(snapshot, column, otherFilters, debouncedQuery || '', searchColumn || null), optionsError: null };
    } catch (err) {
      return { options: null, optionsError: err instanceof SyntaxError ? 'Invalid search pattern' : 'Failed to load filter options.' };
    }
  }, [open, snapshot, column, otherFilters, debouncedQuery, searchColumn]);

  const [selectedValues, setSelectedValues] = useState(new Map());
  const [minDraft, setMinDraft] = useState('');
  const [maxDraft, setMaxDraft] = useState('');
  const [includeBlanksDraft, setIncludeBlanksDraft] = useState(false);
  const [listSearch, setListSearch] = useState('');

  // Re-initialize the draft from the currently committed filter (or sane
  // defaults) whenever the popover opens and options for it have arrived.
  useEffect(() => {
    if (!open || !options) return;
    setListSearch('');
    if (options.kind === 'values') {
      if (activeFilter && activeFilter.kind === 'values' && activeFilter.values) {
        setSelectedValues(new Map(activeFilter.values.map((v) => [String(v), v])));
      } else {
        setSelectedValues(new Map((options.values || []).map((o) => [String(o.value), o.value])));
      }
    } else if (activeFilter && activeFilter.kind === options.kind) {
      setMinDraft(activeFilter.min != null ? String(activeFilter.min) : '');
      setMaxDraft(activeFilter.max != null ? String(activeFilter.max) : '');
      setIncludeBlanksDraft(!!activeFilter.include_blanks);
    } else {
      setMinDraft('');
      setMaxDraft('');
      setIncludeBlanksDraft(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, options && options.kind]);

  const visibleOptions = useMemo(() => {
    const all = (options && options.values) || [];
    if (!listSearch.trim()) return all;
    const needle = listSearch.toLowerCase();
    return all.filter((o) => formatOptionValue(o.value).toLowerCase().includes(needle));
  }, [options, listSearch]);

  const toggleValue = (option) => {
    const key = String(option.value);
    setSelectedValues((prev) => {
      const next = new Map(prev);
      if (next.has(key)) next.delete(key);
      else next.set(key, option.value);
      return next;
    });
  };

  const selectAllVisible = () => {
    setSelectedValues((prev) => {
      const next = new Map(prev);
      visibleOptions.forEach((o) => next.set(String(o.value), o.value));
      return next;
    });
  };

  const clearVisible = () => {
    setSelectedValues((prev) => {
      const next = new Map(prev);
      visibleOptions.forEach((o) => next.delete(String(o.value)));
      return next;
    });
  };

  const applyValues = () => {
    onApply({ column, kind: 'values', values: Array.from(selectedValues.values()) }, column);
    overlayRef.current?.hide();
  };

  const applyRange = () => {
    if (!options || options.kind === 'values') return;
    const min = minDraft.trim() === '' ? null : options.kind === 'date_range' ? minDraft : Number(minDraft);
    const max = maxDraft.trim() === '' ? null : options.kind === 'date_range' ? maxDraft : Number(maxDraft);
    onApply({ column, kind: options.kind, min, max, include_blanks: includeBlanksDraft }, column);
    overlayRef.current?.hide();
  };

  const handleClear = () => {
    onApply(null, column);
    overlayRef.current?.hide();
  };

  return (
    <>
      <Button
        icon="pi pi-filter"
        className={`${isActive ? 'p-button-info' : 'p-button-secondary'} p-button-text p-button-rounded p-button-sm`}
        style={{ width: '1.5rem', height: '1.5rem' }}
        onClick={(e) => {
          e.stopPropagation();
          overlayRef.current?.toggle(e);
        }}
        aria-label={`Filter ${column}`}
      />
      <OverlayPanel ref={overlayRef} onShow={() => setOpen(true)} onHide={() => setOpen(false)} style={{ width: '18rem' }}>
        <div onClick={(e) => e.stopPropagation()}>
          {optionsError && <div className="ex-inline-error mb-2">{optionsError}</div>}

          {options && options.kind === 'values' && (
            <div className="flex flex-column gap-2">
              <InputText value={listSearch} onChange={(e) => setListSearch(e.target.value)} placeholder="Search values…" className="p-inputtext-sm" />
              <div className="flex justify-content-between text-sm">
                <Button label="Select all" className="p-button-link p-button-sm" onClick={selectAllVisible} />
                <Button label="Clear" className="p-button-link p-button-sm" onClick={clearVisible} />
              </div>
              <ScrollPanel style={{ height: '14rem', border: '1px solid var(--app-border)', borderRadius: 10 }}>
                <div className="flex flex-column gap-1 p-2">
                  {visibleOptions.map((option) => {
                    const key = String(option.value);
                    return (
                      <label key={key} className="flex align-items-center gap-2 p-1 border-round" style={{ cursor: 'pointer' }}>
                        <Checkbox checked={selectedValues.has(key)} onChange={() => toggleValue(option)} />
                        <span className="flex-1 white-space-nowrap overflow-hidden text-overflow-ellipsis">{formatOptionValue(option.value)}</span>
                        <span className="text-color-secondary text-sm">{option.count}</span>
                      </label>
                    );
                  })}
                  {visibleOptions.length === 0 && <p className="text-center text-color-secondary p-2">No values</p>}
                </div>
              </ScrollPanel>
            </div>
          )}

          {options && options.kind !== 'values' && (
            <div className="flex flex-column gap-2">
              {options.kind === 'date_range' ? (
                <div className="flex align-items-center gap-2">
                  <Calendar value={isoToDate(minDraft)} onChange={(e) => setMinDraft(e.value ? dateToIso(e.value) : '')} placeholder="Min" dateFormat="yy-mm-dd" className="p-inputtext-sm" />
                  <span className="text-color-secondary">–</span>
                  <Calendar value={isoToDate(maxDraft)} onChange={(e) => setMaxDraft(e.value ? dateToIso(e.value) : '')} placeholder="Max" dateFormat="yy-mm-dd" className="p-inputtext-sm" />
                </div>
              ) : (
                <div className="flex align-items-center gap-2">
                  <InputNumber value={minDraft === '' ? null : Number(minDraft)} onValueChange={(e) => setMinDraft(e.value === null ? '' : String(e.value))} placeholder={options.min != null ? String(options.min) : 'Min'} className="p-inputtext-sm" />
                  <span className="text-color-secondary">–</span>
                  <InputNumber value={maxDraft === '' ? null : Number(maxDraft)} onValueChange={(e) => setMaxDraft(e.value === null ? '' : String(e.value))} placeholder={options.max != null ? String(options.max) : 'Max'} className="p-inputtext-sm" />
                </div>
              )}
              {options.has_blanks && (
                <label className="flex align-items-center gap-2">
                  <Checkbox checked={includeBlanksDraft} onChange={(e) => setIncludeBlanksDraft(e.checked === true)} />
                  <span>Include blank cells</span>
                </label>
              )}
            </div>
          )}

          <div className="flex justify-content-end gap-2 mt-3">
            <Button label="Clear" className="p-button-text p-button-sm" onClick={handleClear} disabled={!isActive} />
            <Button label="Apply" className="p-button-sm" onClick={options && options.kind === 'values' ? applyValues : applyRange} disabled={!options} />
          </div>
        </div>
      </OverlayPanel>
    </>
  );
});

export default ColumnFilterPopover;
